import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { HttpError, HttpService, LC_KEY_PRODUCT_SHORT, LC_KEY_PRODUCT_SHORT_HISTORY_LIST, LC_PRODUCT_SHORT_STORAGE_TIME, LocalStorage } from 'src/toolkit';
import { TTableSort } from 'src/components/table/thead/col';

import { useAppSelector } from '../../hooks';
import { TProductShortList, TProductShortListResponse, TProductShortListQuery, adaptProductShortFromListResponse, TProductShortListSortCol, TProductShortPayloadRequest, adaptProductShortToRequest, TProductShortHistoryItem } from '../../types';
import { BACKEND_PRODUCT_SHORT } from '../../backend-paths';

function saveToLocalStorage(payload: TProductShortPayloadRequest) {
	const historyItemList: TProductShortHistoryItem[] = LocalStorage.get(LC_KEY_PRODUCT_SHORT_HISTORY_LIST) || [];

	const date = new Date();
	const itemKey = JSON.stringify(payload);

	const historyItem: TProductShortHistoryItem = {
		...payload,
		date: date.toISOString(),
		key: itemKey,
	};

	const filtered = historyItemList.filter(item => item.key !== itemKey);

	filtered.unshift(historyItem);

	if (filtered.length > 10) {
		filtered.length = 10;
	}

	LocalStorage.set(LC_KEY_PRODUCT_SHORT_HISTORY_LIST, filtered);
}

export const productShortListGetApi = createAsyncThunk(
	BACKEND_PRODUCT_SHORT,
	(payload: TProductShortPayloadRequest, thunkApi) => {
		const params = adaptProductShortToRequest(payload);

		const key = `${LC_KEY_PRODUCT_SHORT}/${JSON.stringify(params)}`;
		const value = LocalStorage.get(key);

		if (value) {
			const { updatedTimestampAt, result } = value;
			const diff = Date.now() - updatedTimestampAt;

			if (diff <= LC_PRODUCT_SHORT_STORAGE_TIME) {
				console.log('FROM STORAGE');

				saveToLocalStorage(payload);

				return result;
			}
		}

		console.log('FROM DATABASE');

		return HttpService.get<TProductShortListResponse>(BACKEND_PRODUCT_SHORT, { params }).then(response => {
			const { data } = response;
			const result = adaptProductShortFromListResponse(data);

			// LocalStorage.set(key, { updatedTimestampAt: Date.now(), result });

			saveToLocalStorage(payload);

			return result;
		}).catch((error: HttpError) => {
			return thunkApi.rejectWithValue({ ...error });
		});
	},
);

type TInitialState = {
	isLoading: boolean;
	list: TProductShortList;
	showList: TProductShortList;
	query: TProductShortListQuery;
	error?: HttpError;
};

const initialState: TInitialState = {
	isLoading: false,
	list: {
		items: [],
		count: 0,
	},
	showList: {
		items: [],
		count: 0,
	},
	query: {
		limit: 50,
		offset: 0,
		search: undefined,
		searchBrandName: undefined,
		searchSerialNumber: undefined,
		searchProvider: undefined,
		searchName: undefined,
		userNickname: '',
		serialNumber: '',
	},
	error: undefined,
};

export const productShortListSlice = createSlice({
	name: 'productShortListSlice',
	initialState,
	reducers: {
		setProductShortListQueryLimit(state, action: PayloadAction<number>) {
			state.query.limit = action.payload;
		},

		setProductShortListQueryOffset(state, action: PayloadAction<number>) {
			state.query.offset = action.payload;
		},

		setProductShortListQueryShortList(state, action: PayloadAction<TProductShortList>) {
			state.showList = action.payload;
		},

		setProductShortListQuerySearch(state, action: PayloadAction<string>) {
			const search = action.payload || undefined;
			const isSearch = search && search.length > 0;

			if (isSearch && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.search = search;
		},

		setProductShortListQuerySearchBrandName(state, action: PayloadAction<string>) {
			const value = action.payload || undefined;
			const isValue = value && value.length > 0;

			if (isValue && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.searchBrandName = value;
		},

		setProductShortListQuerySearchSerialNumber(state, action: PayloadAction<string>) {
			const value = action.payload || undefined;
			const isValue = value && value.length > 0;

			if (isValue && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.searchSerialNumber = value;
		},

		setProductShortListQuerySearchProvider(state, action: PayloadAction<string>) {
			const value = action.payload || undefined;
			const isValue = value && value.length > 0;

			if (isValue && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.searchProvider = value;
		},

		setProductShortListQuerySearchName(state, action: PayloadAction<string>) {
			const value = action.payload || undefined;
			const isValue = value && value.length > 0;

			if (isValue && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.searchName = value;
		},

		setProductShortListQueryUserNickname(state, action: PayloadAction<string>) {
			const userNickname = action.payload || undefined;
			const isUserNickname = userNickname && userNickname.length > 0;

			state.query.userNickname = isUserNickname ? userNickname : '';
		},

		setProductShortListQuerySerialNumber(state, action: PayloadAction<string>) {
			const serialNumber = action.payload || undefined;

			state.query.serialNumber = serialNumber ? serialNumber : '';
		},

		setProductShortListQuerySort(state, action: PayloadAction<TTableSort>) {
			state.query.sort = action.payload;
		},

		setProductShortListQuerySortCol(state, action: PayloadAction<TProductShortListSortCol>) {
			state.query.sortCol = action.payload;
		},

		clearProductShortList(state) {
			return { ...initialState, list: state.list, query: state.query };
		},
	},

	extraReducers: {
		[productShortListGetApi.pending.type]: (state) => {
			state.isLoading = true;
			state.error = undefined;
		},

		[productShortListGetApi.fulfilled.type]: (state, action: PayloadAction<TProductShortList>) => {
			state.isLoading = false;
			state.list = action.payload;
			state.error = undefined;
		},

		[productShortListGetApi.rejected.type]: (state, action: PayloadAction<HttpError>) => {
			state.isLoading = false;
			state.error = action.payload;
		},
	},
});

export const useProductShortListSelector = () => useAppSelector(state => state[productShortListSlice.name]);

export const { clearProductShortList, setProductShortListQuerySearch, setProductShortListQuerySearchBrandName, setProductShortListQuerySearchSerialNumber, setProductShortListQuerySearchProvider, setProductShortListQuerySearchName, setProductShortListQuerySerialNumber, setProductShortListQueryUserNickname, setProductShortListQuerySort, setProductShortListQuerySortCol, setProductShortListQueryLimit, setProductShortListQueryOffset, setProductShortListQueryShortList } = productShortListSlice.actions;
